import { brandColors } from '@src/assets/colors/brandColors';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Scale,
  Title,
  Tooltip,
} from 'chart.js';
import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export interface DataPoint {
  date: string;
  score: number;
}

interface SummaryGraphChartjsProps {
  graphData: DataPoint[];
  graphLabels: string[];
  label?: string;
  yAxisMax?: number;
}

const SummaryGraphChartjs = ({
  graphData = [],
  label = '会員登録',
  yAxisMax,
  graphLabels,
}: SummaryGraphChartjsProps) => {
  // Y軸のメモリ
  const Y_AXIS_MIN = 0;

  const calculatedYAxisMax = useMemo(() => {
    if (yAxisMax !== undefined) {
      return yAxisMax;
    }

    if (graphData.length === 0) {
      return 100;
    }

    const maxScore = Math.max(...graphData.map((item) => item.score));

    return Math.ceil((maxScore * 1.2) / 100) * 100;
  }, [graphData, yAxisMax]);

  const Y_AXIS_MAX = calculatedYAxisMax;
  const Y_AXIS_STEP = Y_AXIS_MAX <= 500 ? 100 : Math.ceil(Y_AXIS_MAX / 5 / 100) * 100;
  const Y_AXIS_FONT_SIZE = 10;

  // X軸の設定
  const X_AXIS_GRID_COLOR = '#E4E4E4';

  const MONTH_LABELS = graphLabels;
  const MONTH_LABEL_SUFFIX = '';

  // ツールチップのスタイル設定
  const TOOLTIP_STYLES = {
    backgroundColor: '#fff',
    titleColor: '#292929',
    titleFont: {
      size: 10,
      weight: 'normal' as const,
    },
    bodyColor: '#292929',
    bodyFont: {
      size: 14,
    },
    padding: 13,
    borderColor: '#E4E4E4',
    borderWidth: 1,
    cornerRadius: 6,
  };

  // グラフのオプション設定
  const options: ChartOptions<'line'> = {
    responsive: true,
    // アスペクト比固定を解除して高さ固定に
    // maintainAspectRatio: true,
    maintainAspectRatio: false,
    // aspectRatio: 3,
    scales: {
      x: {
        border: {
          display: false,
        },
        grid: {
          color: (context: { chart: ChartJS; tick?: { value: number } }) => {
            const value = context.tick?.value;
            const labels = context.chart.data.labels as string[];
            const label = value !== undefined ? labels[value] : '';
            return MONTH_LABELS.includes(label) ? X_AXIS_GRID_COLOR : 'transparent';
          },
          display: true,
          drawOnChartArea: true,
          drawTicks: false,
          lineWidth: 1,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          color: '#888888',
          callback: function (this: Scale, value: string | number) {
            const label = this.getLabelForValue(value as number);
            if (MONTH_LABELS.includes(label)) {
              const [, month, day] = label.split('-');
              return `${parseInt(month)}/${parseInt(day)}${MONTH_LABEL_SUFFIX}`;
            }
            return '';
          },
        },
      },
      y: {
        min: Y_AXIS_MIN,
        ticks: {
          stepSize: Y_AXIS_STEP,
          font: {
            size: Y_AXIS_FONT_SIZE,
          },
          color: '#888888',
          callback: function (value) {
            return value === 0 ? '' : value;
          },
        },
        grid: {
          color: '#E4E4E4',
          drawTicks: false,
          drawOnChartArea: true,
          lineWidth: 1,
          z: -1,
        },
        border: {
          display: false,
        },
        beginAtZero: true,
        grace: '10%',
        offset: false,
      },
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 0,
      },
      line: {
        borderWidth: 1,
        hoverBorderWidth: 2,
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      tooltip: {
        backgroundColor: TOOLTIP_STYLES.backgroundColor,
        titleColor: TOOLTIP_STYLES.titleColor,
        titleFont: TOOLTIP_STYLES.titleFont,
        bodyColor: TOOLTIP_STYLES.bodyColor,
        bodyFont: TOOLTIP_STYLES.bodyFont,
        borderColor: TOOLTIP_STYLES.borderColor,
        borderWidth: TOOLTIP_STYLES.borderWidth,
        padding: TOOLTIP_STYLES.padding,
        cornerRadius: TOOLTIP_STYLES.cornerRadius,
        displayColors: false,
        callbacks: {
          title: function (context: { label: string }[]) {
            return context[0].label;
          },
          label: function (context: { label: string; parsed: { y: number } }) {
            return `${label}：${context.parsed.y}`;
          },
        },
      },
      legend: {
        display: false,
      },
    },
  };

  const data = {
    labels: graphData.map((item) => item.date),
    datasets: [
      {
        label: label,
        data: graphData.map((item) => item.score),
        borderColor: brandColors['500'],
        tension: 0,
        pointHoverRadius: 10,
        pointHoverBackgroundColor: brandColors['500'],
        pointHoverBorderColor: brandColors['500'],
        pointHoverBorderWidth: 2,
        pointRadius: 0,
        pointBackgroundColor: 'transparent',
        pointBorderColor: 'transparent',
      },
    ],
  };

  return (
    <div style={{ height: '300px' }}>
      <Line
        options={options}
        data={data}
      />
    </div>
  );
};

export default SummaryGraphChartjs;
